import { Component, computed, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AskEvaUserRatingRequest, ConversationUserRatingType } from 'api-clients/ai-api-client';
import { lastValueFrom } from 'rxjs';
import { BannerService } from 'src/shared/banners/banner.service';
import { PreventMultiClickDirective } from 'src/shared/directives/prevent-multi-click.directive';
import { EvaAnswerDto } from '../../shared/models/eva-question-answer.model';
import { EvaQuestionAnswerService } from '../../shared/services/eva-question-answer.service';
import { EvaNegativeFeedbackModalComponent } from '../eva-negative-feedback-modal/eva-negative-feedback-modal.component';

@Component({
  selector: 'ez-eva-feedback',
  imports: [
    PreventMultiClickDirective,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './eva-feedback.component.html',
})
export class EvaFeedbackComponent {
  readonly #dialog = inject(MatDialog);
  readonly #bannerService = inject(BannerService);
  readonly #evaQuestionAnswerService = inject(EvaQuestionAnswerService);
  readonly #selectedRating = signal<ConversationUserRatingType | null | undefined>(undefined);
  protected readonly answer = input<EvaAnswerDto | undefined>();
  protected readonly conversationUserRatingType = ConversationUserRatingType;

  protected readonly userRating = computed<ConversationUserRatingType | null | undefined>(() => {
    const selected = this.#selectedRating();
    return selected !== undefined ? selected : this.answer()?.userRating;
  });

  protected setPositiveFeedback = async (): Promise<void> => {
    if (this.userRating() === ConversationUserRatingType.ThumbsUp) {
      await this.#clearFeedback();
    } else {
      await this.setFeedback(true);
    }
  };

  protected setNegativeFeedback = async (): Promise<void> => {
    if (this.userRating() === ConversationUserRatingType.ThumbsDown) {
      await this.#clearFeedback();
    } else {
      await this.setFeedback(false);
    }
  };


  protected setFeedback = async (isPositive?: boolean): Promise<void> => {
    if (isPositive) {
      try {
        await this.#setConversationUserRating({
          userRating: ConversationUserRatingType.ThumbsUp,
        });
        this.#selectedRating.set(ConversationUserRatingType.ThumbsUp);
      } catch (error) {
        this.#bannerService.addError(error, 'Failed to submit feedback.');
      }
    } else {
      const feedbackData: string | undefined = await lastValueFrom(
        this.#dialog.open(EvaNegativeFeedbackModalComponent, { width: '600px', panelClass: 'eva-theme' }).afterClosed()
      );

      if (feedbackData !== '') {
        try {
          await this.#setConversationUserRating({
            userRating: ConversationUserRatingType.ThumbsDown,
            comment: feedbackData,
          });
          this.#selectedRating.set(ConversationUserRatingType.ThumbsDown);
        } catch (error) {
          this.#bannerService.addError(error, 'Failed to submit feedback.');
        }
      }
    }
  };

  async #setConversationUserRating(rating: AskEvaUserRatingRequest): Promise<void> {
    const askEvaConversationId = this.answer()?.askEvaConversationId;
    if (!askEvaConversationId) {
      return;
    }
    await this.#evaQuestionAnswerService.setConversationUserRating(askEvaConversationId, rating);
  }

  async #clearFeedback(): Promise<void> {
    try {
      await this.#setConversationUserRating({ userRating: ConversationUserRatingType.None });
      this.#selectedRating.set(null);
    } catch (error) {
      this.#bannerService.addError(error, 'Failed to submit feedback.');
    }
  }
}
